import { HttpStatusCode } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { ActivityRemoteControllerService, PdfDocumentRemoteControllerService, ProcessRemoteControllerService, TaskRemoteControllerService } from 'core/api';
import { IActivityDto, IPDFDocumentDto, IProcessDto } from 'core/dto';
import { pairBehaviorSubjects } from 'core/rx-js';
import { LoaderService } from 'core/services';
import { ObjectUtils } from 'core/utils';
import { designerPaths } from 'projects/designer/src/app/designer.paths';
import { DsgDeepTranslationKey } from 'projects/designer/src/app/pipes/translate.pipe';
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs';
import { ITask } from '../../../dto-map/i-task';
import { SignProcessWizardService } from '../../../sign-process-wizard.service';

@Injectable()
export class ProcessReviewService {
  private readonly _destroy = new Subject<void>()


  readonly processIdSubject = new BehaviorSubject<string>(null);
  readonly processSubject = new BehaviorSubject<IProcessDto>(null);
  readonly activitiesSubject = new BehaviorSubject<IActivityDto[]>(null);
  readonly tasksSubject = new BehaviorSubject<ITask[]>(null);
  readonly pdfDocumentsSubject = new BehaviorSubject<IPDFDocumentDto[]>(null);

  constructor(
    @Optional() private readonly _signProcessWizardService: SignProcessWizardService,
    private readonly _processRemoteControllerService: ProcessRemoteControllerService,
    private readonly _taskRemoteControllerService: TaskRemoteControllerService,
    private readonly _activityRemoteControllerService: ActivityRemoteControllerService,
    private readonly _pdfDocumentRemoteControllerService: PdfDocumentRemoteControllerService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly _router: Router
  ) {

    if (this._signProcessWizardService) {
      pairBehaviorSubjects(this._signProcessWizardService.processIdSubject, this.processIdSubject);
      pairBehaviorSubjects(this._signProcessWizardService.processSubject, this.processSubject);
      pairBehaviorSubjects(this._signProcessWizardService.activitiesSubject, this.activitiesSubject);
      pairBehaviorSubjects(this._signProcessWizardService.tasksSubject, this.tasksSubject);
      pairBehaviorSubjects(this._signProcessWizardService.pdfDocumentsSubject, this.pdfDocumentsSubject);
    }
    else {

      this.processIdSubject.pipe(
        distinctUntilChanged(),
        filter(Boolean),
        switchMap((processId) => {

          const id = this._loaderService.show({ key: 'process.load-one', text: 'ss-entities.process.remote-actions.by-key' })

          return this._processRemoteControllerService.getOne(processId).pipe(
            tap({
              error: (response) => {
                const status = response?.error?.error?.status;

                if (status.code === HttpStatusCode.Unauthorized || status.code === HttpStatusCode.Forbidden) {
                  this._router.navigate([designerPaths.unauthorized])
                }
              }
            }),
            switchMap((process) => combineLatest([
              this._taskRemoteControllerService.getByProcess(processId),
              this._activityRemoteControllerService.getByProcess(processId),
              this._pdfDocumentRemoteControllerService.getByProcess(processId)
            ])
              .pipe(
                map(([tasks, activites, pdfDocuments]) => ({ process, tasks, activites, pdfDocuments }))
              ),
            ),
            this._loaderService.disposeByKey('process.load-one')
          )
        }),
        takeUntil(this._destroy)
      )
        .subscribe(({ process, tasks, activites, pdfDocuments }) => {
          this.processSubject.next(process);
          this.tasksSubject.next(tasks.map(taskDto => {
            const type = taskDto.taskType;

            const task = ObjectUtils.cloneDeep(taskDto) as unknown as ITask;
            delete (task as any).taskType
            task.type = type;
            return task;
          }));
          this.activitiesSubject.next(activites);
          this.pdfDocumentsSubject.next(pdfDocuments);
        })
    }
  }

  ngOnDestroy() {
    this._destroy.next()
    this._destroy.complete()
  }
}
